// SYNCED FROM: VidaReactNative/src/features/AddressVerification/types/index.ts
// Keep in sync. See SYNC.md.
import type { LobDeliverability } from '../api/lobApi';

/**
 * Five-field US address. All required fields are strings (empty string while
 * the form is being edited, never null). `addressLine2` is genuinely optional —
 * it's `undefined` when there is no secondary line, not an empty string. The
 * form layer may temporarily hold an empty string while editing; `trimAddress`
 * normalizes it to `undefined` at the submit/verify boundary.
 */
export type Address = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
};

export const ADDRESS_FIELDS = ['addressLine1', 'addressLine2', 'city', 'state', 'zip'] as const;
export type AddressField = (typeof ADDRESS_FIELDS)[number];

/** Where verification is being run from — drives the analytics `surface` payload. */
export type Surface = 'profile' | 'program_unit' | 'onboarding';

/**
 * Verification outcome resolved when the user makes a final decision.
 * `ok: true` → proceed with `address`; `ok: false` → caller should abort.
 * `changed` is true only when the member accepted a Lob suggestion (the address
 * differs from what they typed), so consumers can skip a no-op write-back.
 */
export type VerifyResult = { ok: true; address: Address; changed: boolean } | { ok: false };

/**
 * The three "deliverable but with a unit-related warning" buckets — the only
 * deliverabilities for which we surface the dedicated warning dialog (title +
 * leading line + chosen address).
 */
export type UnitDeliverabilityWarning = Extract<
  LobDeliverability,
  'deliverable_missing_unit' | 'deliverable_unnecessary_unit' | 'deliverable_incorrect_unit'
>;

/**
 * Discriminated state for the verification dialog mounted by
 * `useAddressVerification`. Lives in this types module (rather than next to the
 * hook) so dialog components can `import type { DialogState }` without creating
 * a runtime/lint cycle with the hook file.
 *
 * Each non-idle variant carries the action handlers that drive its terminal
 * outcome. The handlers are closures over the verify() promise's `resolve`,
 * so the dialog components don't need any external `onFinish` plumbing.
 */
export type DialogState =
  | { kind: 'idle' }
  | {
      kind: 'confirm';
      user: Address;
      verified: Address;
      deliverability: LobDeliverability;
      onUseVerified: () => void;
      onUseEntered: () => void;
      onCancel: () => void;
    }
  | {
      kind: 'warning';
      /** The user's typed address. Shown in the dialog body and used as the
       *  result address when this dialog is the terminal step. When the confirm
       *  dialog follows the hook discards this and uses the confirm result
       *  instead. */
      address: Address;
      deliverability: UnitDeliverabilityWarning;
      onContinue: () => void;
      onCancel: () => void;
    }
  | {
      kind: 'undeliverable';
      address: Address;
      footnoteCodes: string[];
      onSubmitAnyway: () => void;
      onClose: () => void;
    }
  | {
      kind: 'error';
      address: Address;
      error: Error;
      onSubmitAnyway: () => void;
      onClose: () => void;
    };
